import { useMemo } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type TileDescriptor } from "@features/metrics-explorer/types";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";

interface Props {
  tile: TileDescriptor;
  index: number;
  onChange(): void;
}

export const LegendItem = ({ tile, index, onChange }: Props) => {
  const { designElementManager } = useDesignElementManagerV2();

  const { tiles, filters, buildMetrics } = useMetricsExplorerContext();

  const metrics = useMemo(() => {
    if (!tile) {
      return [];
    }

    return buildMetrics({
      attributionModel: tile.attributionModel ?? filters.attributionModel,
      attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
      accountingMode: tile.accountingMode ?? filters.accountingMode,
    });
  }, [
    buildMetrics,
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    tile,
    tile?.attributionModel,
    tile?.attributionWindow,
    tile?.accountingMode,
  ]);

  const metric = useMemo(() => {
    return metrics.find((e) => e.id === tile.metricId);
  }, [tile, metrics]);

  const labelColor = designElementManager.getColorForIndex(index);

  const name = tile.name ?? metric?.name;

  return (
    <label
      className="flex items-center cursor-pointer gap-1 m-0"
      onClick={() => onChange()}
    >
      <span
        className={clsx(
          "shrink-0 inline-block rounded-full w-3 h-3 overflow-hidden flex items-center",
          {
            "opacity-50": !tile.visible,
          },
        )}
        style={{ backgroundColor: labelColor }}
      />
      <GenericTooltip
        noInfoCircle
        mouseEnterDelay={1000}
        placement="top"
        content={name}
      >
        <div
          className={clsx("truncate max-w-[260px]", {
            "text-gray+1": !tile.visible,
          })}
        >
          {name}
        </div>
      </GenericTooltip>
    </label>
  );
};
